import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import {
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form4868DueDate = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 4868 Explained: Extending Your Tax Deadline with Ease"
          description="Learn about Form 4868, the key deadlines, and penalties. File quickly and accurately with TaxZerone for a hassle-free 6-month extension."
          keywords=""
          canonical="resources/tax-extension/form-4868-due-dates/"
          ogType="article"
          jsonLdPricing="11.99"
          jsonLdAggregateRatingValue="4.95"
          jsonLdAggregateRatingCount="10859"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 4868</span>
          </span>{' '}
          Due Dates
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-extension/form-4868-due-dates/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>
                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-08-20T09:00:00-05:00"
                      >
                        Aug 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-08-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        Aug 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>
                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 4868 due dates at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',
                      '& li': { mb: 1.6, position: 'relative', pl: 3 },
                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <a
                          href="https://www.taxzerone.com/resources/tax-extension/form-4868-instructions/"
                          style={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Form 4868
                        </a>{' '}
                        must generally be filed by the{' '}
                        <span style={{ fontWeight: 600 }}>
                          original due date of your individual income tax return
                        </span>{' '}
                        to receive an automatic extension of time to file.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        The deadline to file an individual income tax return and
                        Form 4868 is{' '}
                        <span style={{ fontWeight: 600 }}>April 15</span>.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        Filing{' '}
                        <span style={{ fontWeight: 600 }}>
                          Form 4868 by April 15
                        </span>{' '}
                        generally gives taxpayers an{' '}
                        <span style={{ fontWeight: 600 }}>
                          automatic 6-month extension
                        </span>
                        , moving the individual tax return filing deadline to{' '}
                        <span style={{ fontWeight: 600 }}>October 15</span>.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <span style={{ fontWeight: 600 }}>
                          Form 4868 extends the time to file, not the time to
                          pay.
                        </span>{' '}
                        Any estimated tax owed should generally be paid by{' '}
                        <span style={{ fontWeight: 600 }}>April 15</span> to
                        help minimize applicable interest and penalties.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="5" />
                      <span itemProp="name">
                        <a
                          href="https://www.taxzerone.com/tax-extension/efile-irs-form-4868-personal-tax-extension-online/"
                          style={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          E-filing Form 4868
                        </a>{' '}
                        can provide faster processing and confirmation compared
                        with paper filing.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="6" />
                      <span itemProp="name">
                        Taxpayers who{' '}
                        <span style={{ fontWeight: 600 }}>miss the</span>{' '}
                        <a
                          href="https://www.taxzerone.com/resources/tax-extension/form-4868-due-dates/"
                          style={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Form 4868 deadline
                        </a>{' '}
                        may still face applicable penalties and interest if
                        their tax return or tax payment is late.
                      </span>
                    </li>
                  </Box>
                </Box>
                {/* <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is IRS Form 7004?
                </Typography> */}
                {/* <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{ mb: 1 }}
                > */}

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Filing your taxes on time each year is critical to avoiding
                  penalties and interest charges from the IRS. However,
                  sometimes filing by the standard IRS tax deadline can be
                  difficult or impossible. In situations where you need more
                  time to prepare and file your federal tax return, Form 4868
                  will help.
                </Typography>

                <Box sx={{ mb: 1, textAlign: { xs: 'center', md: 'left' } }} />
                {/* </Stack> */}
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is Form 4868?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Federal{' '}
                  <Link
                    href="/resources/tax-extension/form-4868-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 4868
                  </Link>
                  , also known as the Application for Automatic Extension of
                  Time To File U.S. Individual Income Tax Return, is a form used
                  by taxpayers to request an extension of the deadline to file
                  their federal income tax return. Filing this form gives
                  taxpayers an additional 6 months to file their tax return.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This extension is automatic as long as{' '}
                  <Link
                    href="/tax-extension/efile-irs-form-4868-personal-tax-extension-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    IRS Form 4868
                  </Link>{' '}
                  is filled out accurately and fully. You do not have to provide
                  any explanation or reason for requesting the extension.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Filing the extension form only extends the time to file your
                  tax return. It does not extend the deadline to pay any taxes
                  you may owe. You should estimate and pay any owed taxes by the
                  original filing deadline to avoid interest and penalties.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  When is the deadline for filing Form 4868?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  For the 2025 tax year, the deadline to file Form 4868 is April
                  15, 2026. This form must be filed on or before the original
                  due date of your tax return.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you miss the April 15 deadline, the IRS will consider your
                  return late and you may face penalties and interest.
                </Typography>
                <Typography variant="body1">
                  Key dates for the 2025 tax year:
                </Typography>
                <List sx={{ mb: 3 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}> April 15, 2026:</span>{' '}
                      Individual tax returns are due without an extension
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}> April 15, 2026:</span>{' '}
                      Deadline to file Form 4868 for an automatic 6-month
                      extension
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        October 15, 2026:
                      </span>{' '}
                      Extended deadline for filing individual tax returns if
                      Form 4868 was filed by April 15
                    </ListItemText>
                  </ListItem>
                </List>
                {/* <Alert severity="warning" icon={false} sx={{ mb: 5, mt: 0 }}>
                  <b> Note:</b> Note: If your business is required to file Form
                  1041-A, Trust Accumulation of Charitable Amounts, and you want
                  an extension of time, you will need to file Form 8868.
                </Alert> */}

                {/* <Box component={'div'} sx={{ mb: 1 }}>
                  <TableContainer
                    sx={{
                      border: '1px solid #828282',
                      borderCollapse: 'collapse',
                    }}
                  >
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableBody>
                        <TableRow>
                          <TableCell
                            sx={{
                              width: 300,
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              verticalAlign: 'Top',
                            }}
                          >
                            <ul>
                              <li style={{ marginBottom: 16 }}>Form 1042</li>
                              <li style={{ marginBottom: 16 }}>Form 1065</li>
                              <li style={{ marginBottom: 16 }}>Form 1066</li>
                              <li style={{ marginBottom: 16 }}>Form 1120-S</li>
                              <li style={{ marginBottom: 16 }}>Form 3520-A</li>
                              <li style={{ marginBottom: 16 }}>Form 8804</li>
                              <li style={{ marginBottom: 16 }}>Form 8924</li>
                            </ul>
                          </TableCell>
                          <TableCell
                            sx={{
                              width: 400,
                              borderBottom: '1px solid #828282',

                              textAlign: 'center',
                              lineBreak: 1.5,
                            }}
                          >
                            <Typography variant="body1" sx={{ pb: 1 }}>
                              These forms are due by{' '}
                              <b style={{ color: '	#FFA500' }}>March 15, 2024</b>
                              .
                            </Typography>

                            <Typography variant="body1" sx={{ pb: 2 }}>
                              If you want more time to prepare and file these
                              tax forms, you can file an extension.
                            </Typography>

                            <Button
                              variant="contained"
                              sx={{
                                textTransform: 'none',
                                boxShadow: 'none',
                                borderRadius: 2,
                                backgroundColor: '#213e99',
                                color: '#fff',
                                '&:hover, &:focus, &:active': { color: '#fff' },
                              }}
                              size="large"
                              href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=7004duedates`}
                            >
                              E-file Form 7004
                            </Button>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                              Your extended deadline will be{' '}
                              <b style={{ color: '	#1da94a' }}>
                                September 15, 2024
                              </b>
                            </Typography>
                            <Typography variant="body1">
                              (When you e-file Form 7004 with TaxZerone, you
                              will receive a reminder before the extended
                              deadline)
                            </Typography>
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            sx={{
                              width: 300,
                              borderRight: '1px solid #828282',

                              verticalAlign: 'top',
                            }}
                          >
                            <ul>
                              <li style={{ marginBottom: 16 }}>
                                Form 706-GS(T)
                              </li>
                              <li style={{ marginBottom: 16 }}>
                                Form 1041 (bankruptcy estate only)
                              </li>
                              <li style={{ marginBottom: 16 }}>
                                Form 1041 (estate other than a bankruptcy
                                estate)
                              </li>
                              <li style={{ marginBottom: 16 }}>
                                Form 1041 (trust)
                              </li>
                              <li style={{ marginBottom: 16 }}>Form 1041-N</li>
                              <li style={{ marginBottom: 16 }}>
                                Form 1041-QFT
                              </li>
                              <li style={{ marginBottom: 16 }}>Form 1120</li>
                              <li style={{ marginBottom: 16 }}>Form 1120-C</li>
                              <li style={{ marginBottom: 16 }}>Form 1120-F</li>
                              <li style={{ marginBottom: 16 }}>
                                Form 1120-FSC
                              </li>
                              <li style={{ marginBottom: 16 }}>Form 1120-H</li>
                              <li style={{ marginBottom: 16 }}>Form 1120-L</li>
                              <li style={{ marginBottom: 16 }}>Form 1120-ND</li>
                              <li style={{ marginBottom: 16 }}>
                                Form 1120-ND (section 4951 taxes)
                              </li>
                              <li style={{ marginBottom: 16 }}>Form 1120-PC</li>
                              <li style={{ marginBottom: 16 }}>
                                Form 1120-POL
                              </li>
                              <li style={{ marginBottom: 16 }}>
                                Form 1120-REIT
                              </li>
                              <li style={{ marginBottom: 16 }}>
                                Form 1120-RIC
                              </li>
                              <li style={{ marginBottom: 16 }}>Form 1120-SF</li>
                              <li style={{ marginBottom: 16 }}>Form 8928</li>
                            </ul>
                          </TableCell>
                          <TableCell
                            sx={{
                              width: 400,

                              textAlign: 'center',
                              verticalAlign: 'top',
                            }}
                          >
                            <Typography variant="body1" sx={{ pb: 1 }}>
                              These forms are due by{' '}
                              <b style={{ color: '	#FFA500' }}>April 15, 2024</b>
                              .
                            </Typography>

                            <Typography variant="body1" sx={{ pb: 2 }}>
                              If you want more time to prepare and file these
                              tax forms, you can file an extension.
                            </Typography>

                            <Button
                              variant="contained"
                              sx={{
                                textTransform: 'none',
                                boxShadow: 'none',
                                borderRadius: 2,
                                backgroundColor: '#213e99',
                                color: '#fff',
                                mb: 1,
                                '&:hover, &:focus, &:active': { color: '#fff' },
                              }}
                              size="large"
                              href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=7004duedates`}
                            >
                              E-file Form 7004
                            </Button>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                              Your extended deadline will be{' '}
                              <b style={{ color: '	#1da94a' }}>
                                October 15, 2024
                              </b>
                            </Typography>
                            <Typography variant="body1">
                              (When you e-file Form 7004 with TaxZerone, you
                              will receive a reminder before the extended
                              deadline)
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box> */}

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to file Form 4868 for the 2025 tax year?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  There are two ways to file the extension form — electronically
                  or by paper mail.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  E-filing Form 4868 (Recommended by IRS)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  E-filing is the fastest and most accurate way to file the
                  extension. The IRS also recommends that you e-file extension
                  forms for quick processing.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  To{' '}
                  <Link
                    href="/tax-extension/efile-irs-form-4868-personal-tax-extension-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    file Form 4868 online
                  </Link>
                  , you can use an IRS-authorized e-file provider like
                  TaxZerone. You will need to estimate your tax liability and
                  pay any amount due when e-filing.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Paper filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  To paper file Form 4868, download the form from{' '}
                  <Link
                    href="https://www.irs.gov/"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    IRS.gov
                  </Link>
                  . Complete the form with your name, address, Social Security
                  Number, and estimated tax liability. Mail the completed form
                  to the IRS using the correct{' '}
                  <Link
                    href="/resources/tax-extension/form-4868-mailing-addresses-and-filing-guide/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 4868 mailing address
                  </Link>{' '}
                  based on your location.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Pay any estimated tax owed by including a payment with your
                  form.
                </Typography>
                {/* <Alert severity="warning" icon={false} sx={{ mb: 4, mt: 0 }}>
                  <b> Note:</b> If you opt for paper filing, ensure that you
                  submit the form well in advance of the deadline. Keep in mind
                  that it may take some time for the return to reach the IRS.
                </Alert> */}
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Penalties for Form 4868
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }} component="div">
                  The IRS will impose penalties if you
                  <ul style={{ marginBottom: 0 }}>
                    <li style={{ marginBottom: 16 }}>
                      Fail to file your tax return or Form 4868 within the due
                      date
                    </li>
                    <li style={{ marginBottom: 0 }}>
                      Fail to pay the tax due within the due date
                    </li>
                  </ul>
                </Typography>
                <Box component="div" sx={{ mb: 5 }}>
                  <TableContainer
                    sx={{
                      border: '1px solid #828282',
                      borderCollapse: 'collapse',
                    }}
                  >
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ borderBottom: '1px solid #828282' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              borderBottom: '1px solid #828282',
                              borderRight: '1px solid #828282',
                            }}
                          />

                          <TableCell
                            sx={{
                              color: '#444',
                              borderBottom: '1px solid #828282',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Penalty
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            Maximum penalty
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell
                            sx={{
                              width: 300,
                              borderRight: '1px solid #828282',

                              verticalAlign: 'Top',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            <Typography variant="body1" sx={{ pb: 1 }}>
                              Late payment
                            </Typography>
                          </TableCell>
                          <TableCell
                            sx={{
                              width: 400,

                              textAlign: 'left',
                              lineBreak: 1.5,
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            <Typography variant="body1" sx={{ pb: 1 }}>
                              ½ of 1% of the amount due
                            </Typography>
                          </TableCell>
                          <TableCell
                            sx={{
                              width: 400,

                              borderLeft: '1px solid #828282',

                              textAlign: 'left',
                              lineBreak: 1.5,
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            <Typography variant="body1" sx={{ pb: 1 }}>
                              25%
                            </Typography>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              width: 300,
                              borderRight: '1px solid #828282',

                              verticalAlign: 'Top',
                            }}
                          >
                            <Typography variant="body1" sx={{ pb: 1 }}>
                              Late filing
                            </Typography>
                          </TableCell>
                          <TableCell
                            sx={{
                              width: 400,

                              textAlign: 'left',
                              lineBreak: 1.5,
                            }}
                          >
                            <Typography variant="body1" sx={{ pb: 1 }}>
                              5% of the amount due
                            </Typography>
                          </TableCell>
                          <TableCell
                            sx={{
                              width: 400,

                              borderLeft: '1px solid #828282',

                              textAlign: 'left',
                              lineBreak: 1.5,
                            }}
                          >
                            <Typography variant="body1" sx={{ pb: 1 }}>
                              25%
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  E-file Form 4868 with TaxZerone
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  As an IRS-authorized e-file provider, TaxZerone offers the
                  simplest process to{' '}
                  <Link
                    href="/tax-extension/efile-irs-form-4868-personal-tax-extension-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    e-file Form 4868.
                  </Link>
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  <b>Step 1: </b>Select the tax year and enter personal
                  information such as name, address, and SSN.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  <b>Step 2: </b>Calculate the balance due by providing tax
                  liability and payments made.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  <b>Step 3: </b>Transmit your return to the IRS and receive a
                  notification once it is accepted.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Looks simple, right? <br />
              <b>Start your Form 4868 filing</b> <br />
              with TaxZerone now
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=4868duedates`}
              >
                E-file Form 4868
              </Button>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Get your 6-month extension period
              </Typography>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form4868DueDate;
